import type { ReactNode } from 'react';
import { ArcElement, Chart as ChartJS } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import { ArrowDown, ArrowUp, CheckCheck, Code2, FileStack } from 'lucide-react';
import { cn } from '@/lib/utils';
import type {
  CoverageHistoryDashboard,
  CoverageHistoryDashboardMetric,
  CoverageHistoryMode,
  CoverageHistoryTrendPoint,
} from '@/components/smartai/api/getCoverageHistory';
import type { CoverageMetricKind } from './coverage-history-utils';
import { trendMetricsPanelClass } from './coverage-trend-layout';

ChartJS.register(ArcElement);

type MetricCardTheme = {
  surface: string;
  ringTrack?: string;
  iconWell?: string;
};

const METRIC_CARD_THEMES: Record<
  'total' | 'auto' | 'manual' | 'uncovered' | 'executable' | 'covered' | 'files',
  MetricCardTheme
> = {
  total: {
    surface:
      'border-blue-200/80 border-l-[3px] border-l-blue-600 bg-gradient-to-br from-blue-50/95 via-blue-50/50 to-[var(--inv-surface)]',
    ringTrack: '#bfdbfe',
  },
  auto: {
    surface:
      'border-emerald-200/80 border-l-[3px] border-l-emerald-600 bg-gradient-to-br from-emerald-50/95 via-emerald-50/45 to-[var(--inv-surface)]',
    ringTrack: '#bbf7d0',
  },
  manual: {
    surface:
      'border-violet-200/80 border-l-[3px] border-l-violet-600 bg-gradient-to-br from-violet-50/95 via-violet-50/45 to-[var(--inv-surface)]',
    ringTrack: '#ddd6fe',
  },
  uncovered: {
    surface:
      'border-red-200/80 border-l-[3px] border-l-red-600 bg-gradient-to-br from-red-50/90 via-red-50/40 to-[var(--inv-surface)]',
    ringTrack: '#fecaca',
  },
  executable: {
    surface:
      'border-sky-200/80 border-l-[3px] border-l-sky-600 bg-gradient-to-br from-sky-50/95 via-sky-50/45 to-[var(--inv-surface)]',
    iconWell: 'bg-white/80',
  },
  covered: {
    surface:
      'border-emerald-200/80 border-l-[3px] border-l-emerald-600 bg-gradient-to-br from-emerald-50/95 via-emerald-50/50 to-[var(--inv-surface)]',
    iconWell: 'bg-white/80',
  },
  files: {
    surface:
      'border-indigo-200/80 border-l-[3px] border-l-indigo-600 bg-gradient-to-br from-indigo-50/90 via-indigo-50/40 to-[var(--inv-surface)]',
    iconWell: 'bg-white/80',
  },
};

const metricCardShellClass =
  'shadow-[var(--inv-shadow,0_1px_2px_rgba(43,33,24,0.04))] transition-shadow duration-200 hover:shadow-[0_4px_14px_rgba(43,33,24,0.08)]';

type DashboardCoverageSlice = Pick<
  CoverageHistoryDashboard,
  'totalCoverage' | 'testAutomation' | 'manualTests' | 'uncovered'
>;

function dashboardSlice(
  d: CoverageHistoryDashboard,
  kind: CoverageMetricKind,
): DashboardCoverageSlice {
  if (kind === 'inventory') {
    return {
      totalCoverage: d.inventoryTotalCoverage,
      testAutomation: d.inventoryTestAutomation,
      manualTests: d.inventoryManualTests,
      uncovered: d.inventoryUncovered,
    };
  }
  return {
    totalCoverage: d.totalCoverage,
    testAutomation: d.testAutomation,
    manualTests: d.manualTests,
    uncovered: d.uncovered,
  };
}

function pct(m?: CoverageHistoryDashboardMetric): number | null {
  const p = m?.percentage;
  if (p === undefined || p === null || Number.isNaN(Number(p))) return null;
  return Number(p);
}

function ppDelta(
  latest: number | null,
  previous: number | null,
): number | null {
  if (latest == null || previous == null) return null;
  return Math.round((latest - previous) * 10) / 10;
}

function countDelta(latest: number, previous: number | undefined): number | null {
  if (previous === undefined) return null;
  return latest - previous;
}

function TrendFooter({
  delta,
  unit = 'pp',
  showVsLabel = true,
}: {
  delta: number | null;
  unit?: 'pp' | 'count';
  showVsLabel?: boolean;
}) {
  if (delta === null || Number.isNaN(delta)) {
    return showVsLabel ? (
      <p className="m-0 mt-1 text-[0.6875rem] text-[var(--inv-text-muted)]">
        vs. previous build
      </p>
    ) : null;
  }
  const up = delta > 0;
  const down = delta < 0;
  const flat = delta === 0;
  const sign = up ? '+' : '';
  const label =
    unit === 'count' ? `${sign}${delta}` : `${sign}${delta.toFixed(1)} pp`;
  const colorClass = up
    ? 'text-emerald-600'
    : down
      ? 'text-red-600'
      : 'text-[var(--inv-text-secondary)]';
  return (
    <p className="m-0 mt-1 flex flex-wrap items-center gap-1 text-[0.6875rem] text-[var(--inv-text-muted)]">
      {up ? (
        <ArrowUp className={cn('h-3.5 w-3.5 shrink-0', colorClass)} strokeWidth={2.5} />
      ) : null}
      {down ? (
        <ArrowDown className={cn('h-3.5 w-3.5 shrink-0', colorClass)} strokeWidth={2.5} />
      ) : null}
      <span className={cn('font-semibold tabular-nums', colorClass)}>{label}</span>
      {showVsLabel ? <span>vs. previous build</span> : null}
    </p>
  );
}

function DonutRing({
  value,
  color,
  track = '#e8e4df',
}: {
  value: number;
  color: string;
  track?: string;
}) {
  const v = Math.min(100, Math.max(0, value));
  return (
    <div className="relative h-14 w-14 shrink-0">
      <Doughnut
        data={{
          datasets: [
            {
              data: [v, 100 - v],
              backgroundColor: [color, track],
              borderWidth: 0,
            },
          ],
        }}
        options={{
          cutout: '72%',
          responsive: true,
          maintainAspectRatio: true,
          plugins: { legend: { display: false }, tooltip: { enabled: false } },
        }}
      />
      <span
        className="absolute inset-0 flex items-center justify-center text-[0.625rem] font-bold tabular-nums text-[var(--inv-text)]">
        {v.toFixed(0)}%
      </span>
    </div>
  );
}

function CoverageDonutCard({
  title,
  metric,
  deltaPct,
  ringColor,
  theme,
}: {
  title: string;
  metric?: CoverageHistoryDashboardMetric;
  deltaPct: number | null;
  ringColor: string;
  theme: MetricCardTheme;
}) {
  const p = pct(metric) ?? 0;
  return (
    <div
      className={cn(
        'flex h-full min-w-0 gap-3 rounded-xl border p-3',
        theme.surface,
        metricCardShellClass,
      )}>
      <DonutRing
        value={p}
        color={ringColor}
        track={theme.ringTrack ?? '#e8e4df'}
      />
      <div className="min-w-0 flex-1">
        <p className="m-0 text-[0.6875rem] font-medium text-[var(--inv-text-muted)]">
          {title}
        </p>
        <p className="m-0 mt-0.5 text-[1.25rem] font-bold leading-none tabular-nums text-[var(--inv-text)]">
          {pct(metric) != null ? `${pct(metric)!.toFixed(1)}%` : '—'}
        </p>
        <TrendFooter delta={deltaPct} />
      </div>
    </div>
  );
}

function StatIconCard({
  title,
  value,
  subtitle,
  delta,
  icon,
  iconBg,
  theme,
  showVsLabel = false,
}: {
  title: string;
  value: string;
  subtitle?: string;
  delta: number | null;
  icon: ReactNode;
  iconBg: string;
  theme: MetricCardTheme;
  showVsLabel?: boolean;
}) {
  return (
    <div
      className={cn(
        'flex h-full min-w-0 gap-3 rounded-xl border p-3',
        theme.surface,
        metricCardShellClass,
      )}>
      <div
        className={cn(
          'flex h-14 w-14 shrink-0 items-center justify-center rounded-xl ring-1 ring-inset ring-black/[0.06]',
          theme.iconWell ?? iconBg,
        )}>
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <p className="m-0 text-[0.6875rem] font-medium text-[var(--inv-text-muted)]">
          {title}
        </p>
        <p className="m-0 mt-0.5 text-[1.25rem] font-bold leading-none tabular-nums text-[var(--inv-text)]">
          {value}
        </p>
        {subtitle ? (
          <p className="m-0 mt-0.5 text-[0.6875rem] text-[var(--inv-text-secondary)]">
            {subtitle}
          </p>
        ) : null}
        <TrendFooter delta={delta} unit="count" showVsLabel={showVsLabel} />
      </div>
    </div>
  );
}

interface CoverageTrendDashboardMetricsProps {
  mode: CoverageHistoryMode;
  metricKind: CoverageMetricKind | 'both';
  latest?: CoverageHistoryTrendPoint | null;
  previous?: CoverageHistoryTrendPoint | null;
}

export default function CoverageTrendDashboardMetrics({
  mode,
  metricKind,
  latest,
  previous,
}: CoverageTrendDashboardMetricsProps) {
  const coverageKind: CoverageMetricKind =
    metricKind === 'inventory' ? 'inventory' : 'lines';
  const cur = dashboardSlice(latest?.dashboard ?? {}, coverageKind);
  const prev = dashboardSlice(previous?.dashboard ?? {}, coverageKind);

  const dTotal = ppDelta(pct(cur.totalCoverage), pct(prev.totalCoverage));
  const dAuto = ppDelta(pct(cur.testAutomation), pct(prev.testAutomation));
  const dManual = ppDelta(pct(cur.manualTests), pct(prev.manualTests));
  const dUnc = ppDelta(pct(cur.uncovered), pct(prev.uncovered));

  const invCounts = latest?.dashboard ?? {};
  const exec = invCounts.executableLines ?? 0;
  const totalLines = invCounts.totalLines ?? 0;
  const execPct =
    totalLines > 0 ? Math.round((exec / totalLines) * 1000) / 10 : null;

  const covered = invCounts.coveredExecutableLines ?? 0;
  const coveredTotal =
    invCounts.executableLinesInCoverage ??
    (pct(cur.totalCoverage) != null ? cur.totalCoverage?.total : undefined) ??
    0;
  const coveredPct =
    coveredTotal > 0
      ? Math.round((covered / coveredTotal) * 1000) / 10
      : pct(cur.totalCoverage);

  const files = invCounts.totalFiles ?? 0;
  const prevInv = previous?.dashboard ?? {};
  const dFiles = countDelta(files, prevInv.totalFiles);

  const cards: ReactNode[] = [];

  if (mode === 'combined') {
    cards.push(
      <CoverageDonutCard
        key="total"
        title="Total coverage"
        metric={cur.totalCoverage}
        deltaPct={dTotal}
        ringColor="#2563eb"
        theme={METRIC_CARD_THEMES.total}
      />,
      <CoverageDonutCard
        key="auto"
        title="Automation coverage"
        metric={cur.testAutomation}
        deltaPct={dAuto}
        ringColor="#16a34a"
        theme={METRIC_CARD_THEMES.auto}
      />,
      <CoverageDonutCard
        key="manual"
        title="Manual coverage"
        metric={cur.manualTests}
        deltaPct={dManual}
        ringColor="#7c3aed"
        theme={METRIC_CARD_THEMES.manual}
      />,
      <CoverageDonutCard
        key="unc"
        title="Uncovered"
        metric={cur.uncovered}
        deltaPct={dUnc}
        ringColor="#dc2626"
        theme={METRIC_CARD_THEMES.uncovered}
      />,
    );
  } else if (mode === 'automation') {
    cards.push(
      <CoverageDonutCard
        key="total"
        title="Automation coverage"
        metric={cur.totalCoverage}
        deltaPct={dTotal}
        ringColor="#16a34a"
        theme={METRIC_CARD_THEMES.auto}
      />,
      <CoverageDonutCard
        key="unc"
        title="Uncovered"
        metric={cur.uncovered}
        deltaPct={dUnc}
        ringColor="#dc2626"
        theme={METRIC_CARD_THEMES.uncovered}
      />,
    );
  } else {
    cards.push(
      <CoverageDonutCard
        key="total"
        title="Manual coverage"
        metric={cur.totalCoverage}
        deltaPct={dTotal}
        ringColor="#7c3aed"
        theme={METRIC_CARD_THEMES.manual}
      />,
      <CoverageDonutCard
        key="unc"
        title="Uncovered"
        metric={cur.uncovered}
        deltaPct={dUnc}
        ringColor="#dc2626"
        theme={METRIC_CARD_THEMES.uncovered}
      />,
    );
  }

  cards.push(
    <StatIconCard
      key="exec"
      title="Executable lines"
      value={exec.toLocaleString()}
      subtitle={
        totalLines > 0
          ? `of ${totalLines.toLocaleString()}${execPct != null ? ` (${execPct}%)` : ''}`
          : undefined
      }
      delta={countDelta(exec, prevInv.executableLines)}
      icon={<Code2 className="h-6 w-6 text-sky-600" strokeWidth={1.75} />}
      iconBg="bg-sky-100/80"
      theme={METRIC_CARD_THEMES.executable}
      showVsLabel={false}
    />,
    <StatIconCard
      key="covered"
      title="Covered lines"
      value={covered.toLocaleString()}
      subtitle={
        coveredTotal > 0
          ? `of ${coveredTotal.toLocaleString()}${coveredPct != null ? ` (${coveredPct}%)` : ''}`
          : undefined
      }
      delta={countDelta(covered, prevInv.coveredExecutableLines)}
      icon={<CheckCheck className="h-6 w-6 text-emerald-600" strokeWidth={1.75} />}
      iconBg="bg-emerald-100/80"
      theme={METRIC_CARD_THEMES.covered}
      showVsLabel={false}
    />,
    <StatIconCard
      key="files"
      title="Total files"
      value={files.toLocaleString()}
      delta={dFiles}
      icon={<FileStack className="h-6 w-6 text-indigo-600" strokeWidth={1.75} />}
      iconBg="bg-indigo-100/80"
      theme={METRIC_CARD_THEMES.files}
      showVsLabel
    />,
  );

  return (
    <div className={trendMetricsPanelClass}>
      <div
        className="grid grid-cols-1 gap-3 min-[520px]:grid-cols-2 min-[900px]:grid-cols-3 min-[1200px]:grid-cols-4 xl:grid-cols-[repeat(auto-fit,minmax(11rem,1fr))]">
        {cards}
      </div>
    </div>
  );
}
